'use client';

import { useQuery } from '@tanstack/react-query';
import { useUser } from '@clerk/nextjs';
import { fetchItemNetwork } from '@/lib/api';
import { StarRating } from '@/components/favely/star-rating';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, LogIn, BookMarked } from 'lucide-react';
import Link from 'next/link';
import type { NetworkResult } from '@/app/api/items/[itemId]/network/route';

interface NetworkPanelProps {
  itemId: string;
  initialData?: NetworkResult;
}

export function NetworkPanel({ itemId, initialData }: NetworkPanelProps) {
  const { isSignedIn, isLoaded } = useUser();

  const { data, isLoading } = useQuery({
    queryKey: ['item', itemId, 'network'],
    queryFn: () => fetchItemNetwork(itemId),
    enabled: isSignedIn === true,
    initialData,
    // Don't re-fetch on mount when we have server-prefetched data — it's fresh.
    staleTime: initialData ? 5 * 60 * 1000 : 0,
  });

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
            <LogIn className="h-4 w-4 text-secondary" />
          </div>
          <p className="text-sm text-muted-foreground">
            <Link href="/sign-in" className="font-medium text-primary hover:underline">
              Sign in
            </Link>{' '}
            to see who in your network has this.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 shadow-sm space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-3 w-44" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const count = data?.count ?? 0;
  const members = data?.members ?? [];

  if (count === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
          <Users className="h-5 w-5 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">No one yet</p>
        <p className="text-xs text-muted-foreground">
          Be the first in your network to save this.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
      {/* Header count */}
      <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {count} {count === 1 ? 'person' : 'people'} in your network
        </span>
      </div>

      {/* Member list */}
      <ul className="divide-y divide-border">
        {members.map((m) => {
          const displayName = m.user?.displayName ?? m.user?.username ?? m.userId;
          const initial = (displayName?.[0] ?? '?').toUpperCase();
          const hasReview = !!(m as any).reviewText;
          const rating = (m as any).rating as number | undefined;

          return (
            <li key={m.userId} className="px-5 py-4">
              <div className="flex items-start gap-3">
                <Avatar className="h-9 w-9 shrink-0 ring-2 ring-border">
                  <AvatarImage src={m.user?.imageUrl} />
                  <AvatarFallback className="text-xs font-semibold bg-secondary/15 text-secondary">
                    {initial}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1 space-y-1.5">
                  {/* Name + rating row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      href={`/u/${m.userId}`}
                      className="text-sm font-semibold hover:text-primary transition-colors cursor-pointer"
                    >
                      {displayName}
                    </Link>
                    {rating && (
                      <StarRating value={rating} readonly size="sm" />
                    )}
                  </div>

                  {/* Review quote */}
                  {hasReview && (
                    <p className="text-xs text-muted-foreground italic leading-relaxed">
                      &ldquo;{(m as any).reviewText}&rdquo;
                    </p>
                  )}

                  {/* Collections */}
                  {m.collections.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <BookMarked className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                      <span className="text-xs text-muted-foreground">in</span>
                      {m.collections.map((col, i) => (
                        <span key={col._id} className="inline-flex items-center gap-0.5">
                          {(col as any).private ? (
                            <span className="text-xs text-muted-foreground italic">
                              {col.title}
                            </span>
                          ) : (
                            <Link
                              href={`/lists/${col._id}`}
                              className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors cursor-pointer"
                            >
                              {col.title}
                            </Link>
                          )}
                          {i < m.collections.length - 1 && (
                            <span className="text-muted-foreground/40">,</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
